import type { BoardSceneId, LiveLessonSession } from '../../lib/liveLessonTypes';
import { Button, Chip } from '../ui';
import CollapsibleTile from './CollapsibleTile';

type TeacherControlDockProps = {
  workMode: LiveLessonSession['workMode'];
  onWorkModeChange: (mode: LiveLessonSession['workMode']) => void;
  scene: BoardSceneId;
  onSceneChange: (scene: BoardSceneId) => void;
  timerSec: number;
  timerRunning: boolean;
  onToggleTimer: () => void;
  onResetTimer: (seconds: number) => void;
  displayPin: string | null;
  displayPinInput: string;
  onDisplayPinInputChange: (value: string) => void;
  onConnectBoard: () => void;
  onDisconnectBoard: () => void;
};

const WORK_MODES: Array<{ id: LiveLessonSession['workMode']; label: string }> = [
  { id: 'individual', label: 'Einzel' },
  { id: 'pair', label: 'Partner' },
  { id: 'group', label: 'Gruppe' },
  { id: 'plenum', label: 'Plenum' }
];

const SCENES: Array<{ id: BoardSceneId; label: string }> = [
  { id: 'focus', label: 'Fokus' },
  { id: 'task', label: 'Aufgabe' },
  { id: 'timer_only', label: 'Timer' },
  { id: 'progress', label: 'Verlauf' },
  { id: 'room_energy', label: 'Raumenergie' },
  { id: 'quiz', label: 'Quiz' },
  { id: 'blank', label: 'Blank' }
];

function formatDuration(value: number): string {
  const safe = Math.max(0, value);
  const min = Math.floor(safe / 60);
  const sec = safe % 60;
  return `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

export default function TeacherControlDock({
  workMode,
  onWorkModeChange,
  scene,
  onSceneChange,
  timerSec,
  timerRunning,
  onToggleTimer,
  onResetTimer,
  displayPin,
  displayPinInput,
  onDisplayPinInputChange,
  onConnectBoard,
  onDisconnectBoard
}: TeacherControlDockProps) {
  return (
    <CollapsibleTile title="Live-Steuerung" defaultOpen contentClassName="space-y-3">
      <div className="flex flex-wrap gap-2">
        {WORK_MODES.map((entry) => (
          <Button
            key={entry.id}
            size="sm"
            variant={workMode === entry.id ? 'primary' : 'outline'}
            onClick={() => onWorkModeChange(entry.id)}
          >
            {entry.label}
          </Button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        {SCENES.map((entry) => (
          <Button
            key={entry.id}
            size="sm"
            variant={scene === entry.id ? 'primary' : 'outline'}
            onClick={() => onSceneChange(entry.id)}
          >
            {entry.label}
          </Button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="rounded-md border border-gray-300 px-3 py-1 text-sm font-semibold text-gray-800 dark:border-gray-600 dark:text-gray-200">
          {formatDuration(timerSec)}
        </div>
        <Button size="sm" variant="primary" onClick={onToggleTimer}>
          {timerRunning ? 'Pause' : 'Start'}
        </Button>
        <Button size="sm" variant="outline" onClick={() => onResetTimer(5 * 60)}>
          5 min
        </Button>
        <Button size="sm" variant="outline" onClick={() => onResetTimer(10 * 60)}>
          10 min
        </Button>
        <Button size="sm" variant="outline" onClick={() => onResetTimer(15 * 60)}>
          15 min
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3 dark:border-gray-800">
        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Display</span>
        <Chip variant={displayPin ? 'primary' : 'outline'}>{displayPin ? `PIN ${displayPin}` : 'Kein Board'}</Chip>
        <input
          value={displayPinInput}
          onChange={(event) => onDisplayPinInputChange(event.target.value.toUpperCase())}
          className="input-field w-24"
          placeholder="PIN"
        />
        <Button size="sm" variant="outline" onClick={onConnectBoard}>
          Koppeln
        </Button>
        {displayPin ? (
          <Button size="sm" variant="outline" onClick={onDisconnectBoard}>
            Trennen
          </Button>
        ) : null}
      </div>
    </CollapsibleTile>
  );
}
